import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';

interface CopilotPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const contextualPrompts: Record<string, string[]> = {
  '/dashboard': [
    'Summarize today\'s performance',
    'What needs my attention right now?',
    'Compare this week vs last week',
  ],
  '/campaigns': [
    'Draft a promotional campaign message',
    'Which campaign performed best?',
    'Suggest audience for a re-engagement campaign',
  ],
  '/whatsapp-dashboard': [
    'Show unread conversations',
    'What\'s the average response time?',
    'Draft a quick reply template',
  ],
  '/lead-management': [
    'Which leads haven\'t been contacted in 7 days?',
    'Prioritize my leads by conversion likelihood',
    'Draft a follow-up message for cold leads',
  ],
  '/support/tickets': [
    'Summarize open tickets by priority',
    'Which tickets can be auto-resolved?',
    'Draft a response for the oldest ticket',
  ],
  '/analytics': [
    'Why did conversion drop last week?',
    'What\'s our best performing channel?',
    'Generate a weekly summary report',
  ],
  '/inventory-management': [
    'Which products are low on stock?',
    'Suggest a bundle for top sellers',
    'Compare product performance this month',
  ],
};

const defaultPrompts = [
  'Draft a campaign message',
  'Analyze my conversion data',
  'Summarize open tickets',
  'Suggest an automation workflow',
];

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const CopilotPanel: React.FC<CopilotPanelProps> = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const location = useLocation();

  const prompts = contextualPrompts[location.pathname] || defaultPrompts;

  const [isLoading, setIsLoading] = useState(false);

  const handleSend = async (text?: string) => {
    const msg = text || input;
    if (!msg.trim() || isLoading) return;

    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setInput('');
    setIsLoading(true);

    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://web-production-3fcca.up.railway.app';
      const brandId = localStorage.getItem('brand_id') || '';
      const response = await fetch(`${API_URL}/api/v1/chat/brand-copilot`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: msg,
          brand_id: brandId,
          context: location.pathname,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.response || data.message || data.answer || JSON.stringify(data),
        }]);
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `I'm having trouble connecting to the AI service right now. Please try again or check that the backend is running.\n\nYour question: "${msg}"`,
        }]);
      }
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Unable to reach the AI service. The backend may be offline.\n\nYour question was: "${msg}"`,
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed right-0 top-0 bottom-0 w-96 bg-white border-l border-gray-200 shadow-2xl z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-violet-50 to-blue-50">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-gradient-to-br from-violet-500 to-blue-500 rounded-lg flex items-center justify-center">
            <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
            </svg>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">AI Copilot</h3>
            <p className="text-[10px] text-gray-500">Context-aware assistant</p>
          </div>
        </div>
        <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="space-y-4">
            {/* Welcome */}
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-gradient-to-br from-violet-100 to-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <svg className="w-8 h-8 text-violet-500" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                </svg>
              </div>
              <h4 className="text-sm font-semibold text-gray-900">How can I help?</h4>
              <p className="text-xs text-gray-500 mt-1">Ask anything about your brand, data, or workflows</p>
            </div>

            {/* Context-aware suggestions */}
            <div>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2 px-1">
                Suggested for this page
              </p>
              <div className="space-y-1.5">
                {prompts.map((prompt, i) => (
                  <button
                    key={i}
                    onClick={() => handleSend(prompt)}
                    className="w-full text-left px-3 py-2.5 text-sm text-gray-700 bg-gray-50 hover:bg-violet-50 hover:text-violet-700 rounded-lg border border-gray-200 hover:border-violet-200 transition-all"
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>

            {/* Quick actions */}
            <div>
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2 px-1">
                Quick actions
              </p>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: 'Draft campaign', icon: '✉' },
                  { label: 'Analyze data', icon: '📊' },
                  { label: 'Build automation', icon: '⚡' },
                  { label: 'Resolve tickets', icon: '🎫' },
                ].map((action, i) => (
                  <button
                    key={i}
                    onClick={() => handleSend(action.label)}
                    className="flex items-center space-x-2 px-3 py-2 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-violet-50 hover:text-violet-700 hover:border-violet-200 transition-all"
                  >
                    <span>{action.icon}</span>
                    <span>{action.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] px-3 py-2 rounded-xl text-sm ${
                msg.role === 'user'
                  ? 'bg-violet-600 text-white rounded-br-md'
                  : 'bg-gray-100 text-gray-800 rounded-bl-md'
              }`}>
                <p className="whitespace-pre-wrap">{msg.content}</p>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Input area */}
      <div className="border-t border-gray-200 p-3">
        <div className="flex items-center space-x-2">
          <input
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSend()}
            placeholder="Ask anything..."
            className="flex-1 px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-400"
          />
          <button
            onClick={() => handleSend()}
            disabled={isLoading}
            className="p-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors disabled:opacity-50"
          >
            {isLoading ? (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
              </svg>
            )}
          </button>
        </div>
        <p className="text-[10px] text-gray-400 mt-1.5 text-center">
          Press <kbd className="px-1 py-0.5 bg-gray-100 border rounded text-[9px]">Ctrl /</kbd> to toggle
        </p>
      </div>
    </div>
  );
};

export default CopilotPanel;
